// Coach-first rule: Keep coach-only outputs separated from leader routes.
// Do NOT surface coach-only observations to leaders without explicit coach action.
import { ReactNode, forwardRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { getSessionItem, removeSessionItem } from "@/utils/safeStorage";
import { useCoachOnboardingState } from "@/hooks/useCoachOnboardingState";
import { useLeaderOnboardingState } from "@/hooks/useLeaderOnboardingState";

interface ProtectedRouteProps {
  children: ReactNode;
  role?: "coach" | "leader";
  allowIncompleteOnboarding?: boolean;
}

type CalendarSurfacePolicy = {
  requiredProviders: Array<"google" | "microsoft">;
};

export const ProtectedRoute = forwardRef<HTMLDivElement, ProtectedRouteProps>(function ProtectedRoute(
  { children, role, allowIncompleteOnboarding = false },
  ref
) {
  const { user, loading: authLoading, roles: authRoles } = useAuth();
  const location = useLocation();

  // Check for pending invite token in session storage (sessionStorage can be blocked in some privacy modes)
  const { data: pendingInviteToken, isLoading: inviteLoading, error: inviteError } = useQuery({
    queryKey: ["pending-invite-check", user?.id, authRoles.length],
    queryFn: async () => {
      let storedInvite: string | null = null;
      try {
        storedInvite = getSessionItem("pendingInvite");
      } catch {
        return null;
      }

      if (storedInvite) {
        try {
          const { token } = JSON.parse(storedInvite);
          // Validate token exists using RPC
          const { data } = await supabase.rpc("validate_invite_token", { invite_token: token });
          const result = data as unknown as { valid: boolean; coach_name?: string } | null;
          if (result?.valid) {
            return token;
          }
        } catch (e) {
          console.error("Error validating pending invite:", e);
        }

        try {
          removeSessionItem("pendingInvite");
        } catch {
          // ignore
        }
      }
      return null;
    },
    enabled: !!user?.id && !authLoading && authRoles.length === 0,
  });

  const isCoach = authRoles.includes("coach");
  const isLeader = authRoles.includes("leader");
  const calendarAccessMode = role === "coach" ? "scheduling" : role === "leader" ? "discovery" : null;
  const hasCalendarSurfaceRole = role ? authRoles.includes(role) : false;

  const {
    data: calendarSurfacePolicy,
    isLoading: calendarSurfacePolicyLoading,
  } = useQuery<CalendarSurfacePolicy>({
    queryKey: ["calendar-surface-policy", user?.id, role],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("calendar-authorization-status", {
        body: {
          accessMode: calendarAccessMode,
          redirectPath: location.pathname,
        },
      });
      if (error) {
        console.warn("[ProtectedRoute] Calendar surface policy check unavailable; continuing without calendar");
        return { requiredProviders: [] };
      }
      const requiredProviders = Array.isArray(data?.requiredProviders)
        ? data.requiredProviders.filter((provider: unknown) => provider === "google" || provider === "microsoft")
        : [];
      return { requiredProviders };
    },
    enabled: Boolean(user?.id && hasCalendarSurfaceRole && calendarAccessMode && !allowIncompleteOnboarding),
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  // Query coach onboarding status
  const {
    data: coachOnboarding,
    isLoading: coachOnboardingLoading,
    error: coachOnboardingError,
  } = useCoachOnboardingState(user?.id, { enabled: isCoach === true });

  // Query leader onboarding status and check if they have any behaviors (existing user)
  const {
    data: leaderOnboarding,
    isLoading: leaderOnboardingLoading,
    error: leaderOnboardingError,
  } = useLeaderOnboardingState(user?.id, { enabled: isLeader === true });

  // Show loading state
  const isLoading = authLoading ||
    (authRoles.length === 0 && inviteLoading) ||
    (isCoach && coachOnboardingLoading) || 
    (isLeader && leaderOnboardingLoading) ||
    (hasCalendarSurfaceRole && Boolean(calendarAccessMode) && !allowIncompleteOnboarding && calendarSurfacePolicyLoading);

  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const safeCurrentPath =
    currentPath.startsWith("/") && !currentPath.startsWith("//")
      ? currentPath
      : null;
  const authRedirectTarget = safeCurrentPath
    ? `/auth?next=${encodeURIComponent(safeCurrentPath)}`
    : "/auth";
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <span className="text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  if (inviteError || coachOnboardingError || leaderOnboardingError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3">
          <h2 className="text-lg font-semibold text-foreground">We hit a loading error</h2>
          <p className="text-muted-foreground">
            Please refresh the page or sign out and back in. If the error persists, contact support.
          </p>
          <button
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            onClick={() => window.location.reload()}
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to auth
  if (!user) {
    return <Navigate to={authRedirectTarget} replace />;
  }

  // User has no roles - redirect to /post-auth to resolve
  if (authRoles.length === 0) {
    // Check for pending invite token - redirect to complete signup
    if (pendingInviteToken) {
      return <Navigate to={`/join/${pendingInviteToken}`} replace />;
    }

    // Check for pending signup role in sessionStorage
    let pendingSignupRole: string | null = null;
    try {
      pendingSignupRole = getSessionItem("signupRole");
    } catch {
      pendingSignupRole = null;
    }
    if (pendingSignupRole) {
      return <Navigate to="/post-signup" replace />;
    }

    console.log("[ProtectedRoute] No roles found, redirecting to /post-auth");
    return <Navigate to={safeCurrentPath ? `/post-auth?next=${encodeURIComponent(safeCurrentPath)}` : "/post-auth"} replace />;
  }

  // Role-specific access checks (when a specific role is required)
  if (role) {
    const hasRequiredRole = authRoles.includes(role);

    if (!hasRequiredRole) {
      if (isCoach) {
        return <Navigate to="/coach/leaders" replace />;
      }
      if (isLeader) {
        return <Navigate to="/today" replace />;
      }
      return <Navigate to="/auth" replace />;
    }

    if (
      calendarAccessMode &&
      !allowIncompleteOnboarding &&
      calendarSurfacePolicy?.requiredProviders.length
    ) {
      const decisionPath = `/calendar-reauthorization?next=${encodeURIComponent(safeCurrentPath ?? location.pathname)}&access_mode=${calendarAccessMode}`;
      return <Navigate to={decisionPath} replace />;
    }

    // Coach onboarding gate removed — setup now happens inline on the Overview page

    // Leader-specific onboarding check
    if (role === "leader" && isLeader && !allowIncompleteOnboarding) {
      const onboardingComplete = leaderOnboarding?.hasOnboarding === true;

      if (!onboardingComplete) {
        // No onboarding completed - redirect to onboarding
        if (location.pathname !== "/onboarding") {
          return <Navigate to="/onboarding" replace />;
        }
      }
    }
  }

  // Authenticated and authorized - render children
  return <div ref={ref}>{children}</div>;
});
